import { useMemo } from "react";
import { useEntitlements } from "../../contexts";

/** The plan feature behind Orders → Lookbooks and "+ New Lookbook". */
export const REQUEST_LOOKBOOK_FEATURE = "request_lookbook_generation";

/** Shared Orders section tabs — used by Requests + Cart Orders via SubTopbar */
export const ORDERS_TABS = [
  { label: "Orders", to: "/requests/orders", exact: true },
  // Merchant Orders is hidden, not deleted: /requests/merchant still routes and
  // still works for anyone holding the link. It was a tab nobody on this team
  // uses, taking a quarter of the tab strip on a phone.
  { label: "Cart Orders", to: "/orders", exact: true },
  { label: "Lookbooks", to: "/requests/lookbooks", exact: true, feature: REQUEST_LOOKBOOK_FEATURE },
];

/** ORDERS_TABS minus any tab the store's plan does not include. */
export function useOrdersTabs() {
  const { hasFeature } = useEntitlements();
  return useMemo(
    () => ORDERS_TABS.filter((tab) => !tab.feature || hasFeature(tab.feature)),
    [hasFeature]
  );
}

/**
 * "+ New Lookbook" — the third header action, shown only where a request
 * lookbook can actually be built or browsed (the Orders list and the Lookbooks
 * tab). It is not part of ORDERS_ACTIONS because Merchant Orders and Cart
 * Orders are not request lists you can select from.
 *
 * On the Orders page it opens the selection overlay in place rather than
 * navigating — filters and search have to keep working underneath it — so the
 * page passes its own `onClick` and this is only the shape.
 */
export const NEW_LOOKBOOK_ACTION = {
  label: "+ New Lookbook",
  // Secondary (grey), matching "+ New Lookbook" on Manage Listings. The two
  // deal-starting buttons beside it are the page's primary actions; three
  // purple CTAs in a row read as three equal choices, which they are not.
  variant: "secondary",
  size: "lg",
  className: "rc-ui-btn--hero",
  "data-btn": "request-lookbook-new",
};

/**
 * The two ways a rep starts a deal from this page.
 *
 * They now open the purpose-built variants rather than the generic flows:
 * SOURCING ENQUIRY (/request/sourcing) and SELL ENQUIRY (/request/sell-enquiry).
 * Those are the same order / sell flows with the specced differences — SEARCH
 * PRODUCT opening on Database, Previous Purchases available on a sell, and the
 * renamed CONFIRM REQUEST button row (Place Order / Place Request, Add Trade-In
 * / PX, Add More…). See requests/flowVariants.js.
 *
 * The generic flows are untouched and still reachable at /request/order and
 * /request/sell — nothing about them changed, they just no longer have a button
 * here.
 *
 * Still the only TWO deal-starting buttons. The Orders page adds a third,
 * NEW_LOOKBOOK_ACTION (below) — the sub-bar was laid out for two side-by-side
 * actions, and the third fits because PageHeader already collapses everything
 * past the first into a ⋯ menu on phones, where the stacking rule in rc-ui.scss
 * applied. A fourth would need that layout revisited. RECORD A SALE is not here
 * on purpose either: it starts from a listing, so its entry point is the listing
 * card's own "Record Sale" action.
 *
 * `data-btn` ids are unchanged (`order-new` / `sell-new`): they identify the
 * slot, not the label, and e2e/styleguide docs key off them.
 */
export const ORDERS_ACTIONS = [
  { label: "+ Sourcing Enquiry", to: "/request/sourcing", variant: "primary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "order-new" },
  { label: "+ Sell Enquiry", to: "/request/sell-enquiry", variant: "primary", size: "lg", className: "rc-ui-btn--hero", "data-btn": "sell-new" },
];
